import { useSectionInView } from './useSectionInView';

/**
 * **FI-46bis** — Hook che incapsula la risoluzione "sezione corrente" del
 * builder a partire dallo scroll-spy + array statico delle sezioni.
 * Estratto da `BuilderPageInner` per rispettare `complexity: 10` e
 * `max-lines-per-function: 100`.
 *
 * Restituisce:
 *  - `currentId`: id della sezione in viewport (o `firstSection.id` come fallback)
 *  - `safeIndex`: indice in `sections`, clampato a 0 se non trovato
 *  - `currentSection`: la `SectionDef` corrispondente
 *
 * **Pre-condizione**: `sections` non vuoto (throw se vuoto, errore di
 * configurazione del page).
 */
export function useCurrentBuilderSection<T extends { id: string }>(
  sections: readonly T[],
  draftId: string,
): { currentId: string; safeIndex: number; currentSection: T } {
  const sectionIds = sections.map((s) => s.id);
  // FI-68: passa `draftId` come remountKey per re-osservare nuovi DOM elements
  // dopo il `key={draft.id}` remount di SectionList post-RESET.
  const inViewId = useSectionInView(sectionIds, draftId);
  const firstSection = sections[0];
  if (!firstSection) {
    throw new Error('useCurrentBuilderSection: `sections` deve avere almeno una entry.');
  }
  const currentId = inViewId ?? firstSection.id;
  const currentIndex = sections.findIndex((s) => s.id === currentId);
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;
  const currentSection = sections[safeIndex] ?? firstSection;
  return { currentId, safeIndex, currentSection };
}
